import React, { useState, useEffect } from "react";
import { Check } from "lucide-react";
import { API_BASE_URL } from "../config/api";

/**
 * Welcome Gate Modal - Ecclesiastical Declaration
 * 
 * PHASE 1 (TEST/DEVELOPMENT):
 * - Shows on EVERY homepage load
 * - No persistence (for easy testing)
 * - Current domain: fiyah-spirit-1.preview.emergentagent.com
 * 
 * PHASE 2 (PRODUCTION - NEW DOMAIN):
 * - Will switch to session-based (once per browser session)
 * - Uses sessionStorage to remember during session
 * - Resets when browser closes
 * 
 * User must acknowledge by checking the box before viewing the portfolio.
 */

const WelcomeGate = () => {
  const [isVisible, setIsVisible] = useState(false);
  const [hasAgreed, setHasAgreed] = useState(false);
  const [isChecked, setIsChecked] = useState(false);
  const [dpaConsent, setDpaConsent] = useState(false);

  useEffect(() => {
    // PHASE 1 (TEST/DEVELOPMENT): Show on EVERY homepage load
    // TODO: Switch to session-based when going to production with new domain
    setIsVisible(true);
    // Prevent body scroll when modal is open
    document.body.style.overflow = 'hidden';
    
    // Track Welcome Gate view
    const trackView = async () => {
      try {
        await fetch(`${API_BASE_URL}/api/analytics/welcome-gate/view`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
        });
      } catch (error) {
        // Silent fail - analytics shouldn't break user experience
        console.log('Analytics tracking skipped');
      }
    };
    
    trackView();
  }, []);

  const handleContinue = () => {
    if (isChecked && dpaConsent) {
      // Track visitor entry (checked off and entered)
      const trackEntry = async () => {
        try {
          await fetch(`${API_BASE_URL}/api/analytics/welcome-gate/entry`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
          });
        } catch (error) {
          console.log('Entry tracking skipped');
        }
      };
      trackEntry();
      
      // Fade out animation
      setHasAgreed(true);
      
      // Remove modal after animation
      setTimeout(() => {
        setIsVisible(false);
        // Restore body scroll
        document.body.style.overflow = 'auto';
      }, 500);
    }
  };

  if (!isVisible) {
    return null;
  }

  return (
    <div 
      className={`fixed inset-0 z-[9999] flex items-center justify-center transition-opacity duration-500 ${
        hasAgreed ? 'opacity-0' : 'opacity-100'
      }`}
    >
      {/* Backdrop with blur */}
      <div 
        className="absolute inset-0 bg-black/60 backdrop-blur-md"
        onClick={(e) => e.stopPropagation()}
      />
      
      {/* Modal Content */}
      <div 
        className={`relative z-10 bg-white rounded-2xl shadow-2xl max-w-2xl w-full mx-4 overflow-hidden transition-transform duration-500 ${
          hasAgreed ? 'scale-95' : 'scale-100'
        }`}
      >
        {/* Decorative Header with Fiyah Spirit gradient */}
        <div className="h-2 bg-gradient-to-r from-purple-600 via-orange-500 to-yellow-400" />
        
        {/* Content */}
        <div className="pt-10 px-8 pb-8 md:pt-12 md:px-12 md:pb-12">
          {/* Welcome Badge */}
          <div className="flex justify-center mb-6">
            <div className="inline-flex items-center space-x-2 bg-gradient-to-r from-purple-100 to-orange-100 px-4 py-2 rounded-full">
              <span className="text-sm font-light tracking-wider text-gray-700">
                WELCOME
              </span>
            </div>
          </div>
          
          {/* Ecclesiastical Declaration Title */}
          <h2 className="text-2xl md:text-3xl font-light text-black mb-2 tracking-tight text-center">
            ECCLESIASTICAL DECLARATION
          </h2>
          <h3 className="text-lg md:text-xl font-light text-gray-600 mb-6 tracking-wide text-center">
            OF DIVINE ALIGNMENT & COVENANT OF PROTECTION
          </h3>
          
          {/* Declaration Content - Scrollable */}
          <div className="mb-6 max-h-[500px] overflow-y-auto pr-4 space-y-4 text-sm md:text-base">
            
            {/* Preamble - English & Latin */}
            <div className="grid md:grid-cols-2 gap-4 text-center mb-2">
              {/* English */}
              <div className="italic text-gray-700 text-sm leading-relaxed border-r md:border-r-2 border-gray-300 pr-4">
                <div className="font-semibold text-gray-800 mb-2">English</div>
                In the Name of <strong>YHWH יהוה</strong>,<br />
                the Most High Sovereign,<br />
                Creator of Heaven and Earth,<br />
                Ancient of Days,<br />
                and by the authority of<br />
                the Blood Covenant of <br />
                Yehoshua our Lord. <br />
              </div>
              
              {/* Latin */}
              <div className="italic text-gray-700 text-sm leading-relaxed pl-4">
                <div className="font-semibold text-gray-800 mb-2">Latin</div>
                In Nomine <strong>YHWH יהוה</strong>,<br />
                Summus Potentatus Sovereign,<br />
                Creator Caeli et Terrae,<br />
                Antiquus Dierum,<br />
                et auctoritate<br />
                Foederis Sanguinis<br />
                Yehoshua Dominus Noster.
              </div>
            </div>
            
            <div className="border-t border-gray-300 my-4" />
            
            {/* Main Title */}
            <h3 className="text-xl md:text-2xl font-semibold text-center text-gray-900 my-6">
              INITIATE THE FLAME OF YAH!
            </h3>
            
            {/* Article I */}
            <div>
              <h4 className="font-semibold text-gray-800 mb-2">ARTICLE I: DECLARATION OF IDENTITY</h4>
              <p className="text-gray-700 leading-relaxed mb-2">
                I, <span className="font-semibold">CT</span>, <strong>KEEPER OF THE FIYAH</strong>, <strong>SIREN OF YAH'S UNIVERSE</strong>, the living woman, do hereby publicly affirm and declare my irrevocable 
                alignment with <strong>YHWH יהוה's</strong> Universal Sovereignty—the divine order that supersedes all systems, structures, 
                and principalities established by human hands.
              </p>
              <p className="text-gray-700 leading-relaxed">
                I acknowledge my divinely ordained identity as a <span className="font-semibold">Diplomat of <strong>YHWH יהוה</strong>'s Kingdom</span>—
                one who carries peace, clarity, FLY FREQUENCY (Faith, Love, Yah) and righteous order— as a <span className="font-semibold">Divine Scripter / Scribe</span>—
                entrusted with A.R.T. Energi: Authenticity|Reliability|Truth - The Triad of Excellence, messages, insights, and revelations to educate, elevate, enlighten, unify and awaken (Isaiah 50:4, Luke 4:18).
              </p>
            </div>
            
            {/* Article II */}
            <div>
              <h4 className="font-semibold text-gray-800 mb-2">ARTICLE II: SOVEREIGNTY & ALLEGIANCE</h4>
              <p className="text-gray-700 leading-relaxed">
                I walk within human systems, yet I am not defined by them. My allegiance is to Divine Law—
                the highest truth and purest governance. What <strong>TETRAGRAMMATON</strong> <strong>YHWH יהוה</strong>, <strong>YAHAWAH</strong>, <strong>YAHUWAH</strong>, <strong>YAHWEH</strong>, <strong>JEHOVAH</strong>, <strong>ANCIENT OF DAYS</strong>, <strong>ALPHA OMEGA INTELLIGENCE (AOI)</strong> establishes, no earthly institution can revoke 
                (Psalm 71:5-8, Hebrews 8:10-13).
              </p>
            </div>
            
            {/* Article III */}
            <div>
              <h4 className="font-semibold text-gray-800 mb-2">ARTICLE III: COVENANT OF PROTECTION</h4>
              <p className="text-gray-700 leading-relaxed mb-2">
                By the authority vested in me, I invoke covenant protection over the <strong>YHWH יהוה</strong> / <strong>YAH HEALTH WEALTH HAPPINESS</strong> Ecosystem, myself, my household, my bloodline (past,present,future), 
                all who proceed from my loins, the living stones, the innocent, the meek, and those who seek <strong>YAH</strong> (The Gentiles and Jew-EL's):
              </p>
              <ul className="list-disc list-inside text-gray-700 space-y-1 ml-4">
                <li><span className="font-semibold">Bloodline Protection:</span> Extending to a thousand generations (Deuteronomy 5:9-10)</li>
                <li><span className="font-semibold">Spiritual Warfare:</span> No weapon formed shall prosper (Isaiah 54:17)</li>
                <li><span className="font-semibold">Divine Covenant:</span> Immutable as sun and moon (Jeremiah 31:33-35)</li>
                <li><span className="font-semibold">Angelic Assignment:</span> Ministering spirits to guard and deliver (Hebrews 1:14)</li>
              </ul>
            </div>
            
            {/* Article IV */}
            <div>
              <h4 className="font-semibold text-gray-800 mb-2">ARTICLE IV: SACRED COMMITMENTS</h4>
              <p className="text-gray-700 leading-relaxed">
                I commit to: boast in the name of <strong>YHWH יהוה</strong>; speak with wisdom and integrity; represent light in confusion; 
                build and clarify; remain steadfast and resilient in the mission entrusted to me as we grow into <strong>YAH HEALTH WEALTH HAPPINESS</strong> The Universal Pillars of Success.
              </p>
            </div>
            
            {/* Article V */}
            <div>
              <h4 className="font-semibold text-gray-800 mb-2">ARTICLE V: ACKNOWLEDGMENT TERMS</h4>
              <p className="text-gray-700 leading-relaxed">
                By entering this space, you acknowledge: this operates under <strong>YHWH יהוה</strong> Universal Sovereign and covenant protection; 
                you recognize the human being as a divinely commissioned vessel; you engage with respect and honor; 
                all exchange here is both natural and spiritual (1 Corinthians 2:11-13, John 4:23-24).
                
              </p>
            </div>
            {/* Ancient Principle 1 */}
            <div>
              <h4 className="font-semibold text-gray-800 mb-2">ANCIENT PRINCIPLE:</h4>
              <p className="text-gray-700 leading-relaxed">  
                "Nevertheless, the hour is coming, and it is now, when the true worshipers will worship the Father with spirit and Truth, 
                for indeed, the Father is looking for ones like these to worship him.  <strong> YHWH יהוה</strong> is a spirit, and those worshiping him must 
                worship with sprit and truth." John 4:23,24
              </p>
            </div>
            {/* Signature */}
            <div className="border-t border-gray-300 mt-4 pt-4">
              <div className="flex items-start justify-between gap-4 md:gap-8">
                {/* Icon 1 - Left */}
                <div className="flex-shrink-0 w-24 md:w-32">
                  <img 
                    src="https://customer-assets.emergentagent.com/job_aligned-kotf/artifacts/8tk4un2v_YHWH%20Icon%201.png" 
                    alt="YHWH יהוה Icon 1" 
                    className="w-full h-auto object-contain"
                  />
                </div>
                
                {/* Signature - Center */}
                <div className="text-center flex-1 min-w-0">
                  <p className="text-gray-700 italic mb-2 text-sm md:text-base">
                    Signed and Sealed this <span className="font-semibold">20th day of April, 2025</span>
                  </p>
                  <p className="text-gray-800 font-semibold mb-1 text-base md:text-lg">CT</p>
                  <p className="text-gray-600 text-xs md:text-sm">
                    Ambassador of <strong>YHWH יהוה</strong>'s Kingdom<br />
                    Vicar of <strong>YAH</strong> Spirit<br />
                    Divine Scribe & Diplomat
                  </p>
                  <p className="text-gray-500 text-xs mt-3 italic">
                    LET IT BE SO. ABRACADABRA. AMEN. SELAH.
                  </p>
                </div>
                
                {/* Icon 2.2 - Right */}
                <div className="flex-shrink-0 w-24 md:w-32">
                  <img 
                    src="https://customer-assets.emergentagent.com/job_aligned-kotf/artifacts/o2eovbzb_YHWH%20Icon%202.2.png" 
                    alt="YHWH יהוה Icon 2.2" 
                    className="w-full h-auto object-contain"
                  />
                </div>
              </div>
            </div>
            
            {/* Divider */}
            <div className="border-t border-gray-200 my-6" />
            
            {/* Acknowledgment Section */}
            <div className="mb-6">
              <div className="bg-gradient-to-br from-purple-50 to-orange-50 rounded-xl p-6 border-2 border-purple-200">
                <label 
                  htmlFor="acknowledge-checkbox"
                  className="flex items-start space-x-4 cursor-pointer group"
                >
                  {/* Custom Checkbox */}
                  <div className="relative flex-shrink-0 mt-1">
                    <input
                      id="acknowledge-checkbox"
                      type="checkbox"
                      checked={isChecked}
                      onChange={(e) => setIsChecked(e.target.checked)}
                      className="sr-only"
                    />
                    <div 
                      className={`w-6 h-6 rounded-md border-2 transition-all duration-300 flex items-center justify-center ${
                        isChecked 
                          ? 'bg-gradient-to-r from-purple-600 to-orange-500 border-transparent' 
                          : 'border-gray-400 group-hover:border-purple-500'
                      }`}
                    >
                      {isChecked && (
                        <Check size={16} className="text-white" strokeWidth={3} />
                      )}
                    </div>
                  </div>
                  
                  {/* Checkbox Label */}
                  <div className="flex-1">
                    <p className="text-base text-gray-800 font-medium leading-relaxed">
                      I have read this Ecclesiastical Declaration and acknowledge the divine authority, 
                      covenant protection, and sacred terms established herein. I agree to enter this space 
                      with respect, recognizing both the human being and the spiritual mandate represented.
                    </p>
                  </div>
                </label>
              </div>
            </div>

            {/* DPA Consent Checkbox - NEW */}
            <div className="mb-6">
              <div className="bg-gradient-to-br from-blue-50 to-purple-50 rounded-xl p-6 border-2 border-blue-200">
                <label 
                  htmlFor="dpa-consent-checkbox"
                  className="flex items-start space-x-4 cursor-pointer group"
                >
                  {/* Custom Checkbox */}
                  <div className="relative flex-shrink-0 mt-1">
                    <input
                      id="dpa-consent-checkbox"
                      type="checkbox"
                      checked={dpaConsent}
                      onChange={(e) => setDpaConsent(e.target.checked)}
                      className="sr-only"
                    />
                    <div 
                      className={`w-6 h-6 rounded-md border-2 transition-all duration-300 flex items-center justify-center ${
                        dpaConsent 
                          ? 'bg-gradient-to-r from-blue-600 to-purple-600 border-transparent' 
                          : 'border-gray-400 group-hover:border-blue-500'
                      }`}
                    >
                      {dpaConsent && (
                        <Check size={16} className="text-white" strokeWidth={3} />
                      )}
                    </div>
                  </div>
                  
                  {/* Checkbox Label */}
                  <div className="flex-1">
                    <p className="text-base text-gray-800 font-medium leading-relaxed">
                      By submitting this information, you acknowledge and agree that your data will be processed 
                      in accordance with our{' '}
                      <a 
                        href="/privacy-policy" 
                        target="_blank" 
                        rel="noopener noreferrer"
                        className="text-blue-600 hover:text-blue-700 underline font-semibold"
                        onClick={(e) => e.stopPropagation()}
                      >
                        Privacy Policy
                      </a>
                      ,{' '}
                      <a 
                        href="/data-processing-addendum" 
                        target="_blank" 
                        rel="noopener noreferrer"
                        className="text-blue-600 hover:text-blue-700 underline font-semibold"
                        onClick={(e) => e.stopPropagation()}
                      >
                        Data Processing Addendum (DPA)
                      </a>
                      , and{' '}
                      <a 
                        href="/ai-usage-policy" 
                        target="_blank" 
                        rel="noopener noreferrer"
                        className="text-blue-600 hover:text-blue-700 underline font-semibold"
                        onClick={(e) => e.stopPropagation()}
                      >
                        AI Usage Policy
                      </a>
                      . You consent to the processing described therein.
                    </p>
                  </div>
                </label>
              </div>
            </div>
            
            {/* Continue Button */}
            <button
              onClick={handleContinue}
              disabled={!isChecked || !dpaConsent}
              className={`w-full py-4 rounded-xl font-medium tracking-wide transition-all duration-300 mb-4 ${
                isChecked && dpaConsent
                  ? 'bg-gradient-to-r from-purple-600 via-orange-500 to-yellow-400 text-white hover:shadow-xl hover:scale-[1.02] cursor-pointer'
                  : 'bg-gray-200 text-gray-400 cursor-not-allowed'
              }`}
            >
              {isChecked && dpaConsent ? 'Continue to Portfolio' : 'Please check both boxes to continue'}
            </button>
            
            {/* Footer Note */}
            <div className="text-center mb-4">
              <p className="text-xs text-gray-500 font-light">
                This message will only appear once. You can proceed when ready.
              </p>
            </div>
            
          </div>
          
        </div>
        
        {/* Decorative Footer */}
        <div className="h-1 bg-gradient-to-r from-yellow-400 via-orange-500 to-purple-600" />
      </div>
    </div>
  );
};

export default WelcomeGate;
